// Filter Quick-Apply Widget
// 仅 Admin 可见，允许一次性选择多个 Filter 后一起提交，页面只刷新一次
// Redmine #18570

import '../styles/components/filter/filter-quick-apply.less'
import { ready } from '@/lib/ready'
import { getUrlParameter } from '@/lib/utils'

declare const _isRepUser: boolean | undefined

// ---- 类型定义 ----
interface FilterItem {
  Title: string
  fil: string
}
interface FilterType {
  Title: string
  SelectedType: 'Radio' | 'CheckBox'
  FilterItems: FilterItem[]
}
interface SortItem {
  Title: string
  fil: string
}
interface FilterData {
  Filters: FilterType[]
  Sorts: SortItem[]
}
interface Selection {
  typeTitle: string
  typeLabel: string
  valueLabel: string
  fil: string
}
interface ComboItem {
  label: string
  value: string
}

ready(() => {
  // ---- 初始化条件 ----
  if (typeof _isRepUser === 'undefined' || !_isRepUser) return
  if (!document.querySelector('.leftfilter-master')) return

  // ---- 状态 ----
  let FilterData: FilterData | null = null
  let filterSelections: Selection[] = [] // [{typeTitle, typeLabel, valueLabel, fil}]
  let sortSelection: { label: string; fil: string } | null = null // e.g. fil="sortby=12"

  // ---- 工具函数 ----
  // 当前页面 base key：路径 + 可选的 searchterm 参数
  function getBaseKey(): string {
    const path = location.pathname
    const st = (getUrlParameter('searchterm') || '').trim()
    return st ? path + '?searchterm=' : path
  }

  // fil 是否为独立参数（如 preproduction=1、sortby=12），以 & 追加，不参与 - 拼接
  function isIndependentParam(fil: string): boolean {
    return fil.indexOf('=') >= 0
  }

  // ---- 注入 Widget HTML ----
  function injectWidget(): void {
    // ltlNarrowSearch 渲染的第一个元素是 .mobile-narrow（SolrPShowManager.cs:488）
    let anchor = document.querySelector<HTMLElement>('.right-items .mobile-narrow,.best-sellers__main .mobile-narrow')
    if (!anchor) {
      anchor = document.querySelector<HTMLElement>('.right-items .sortby-box,.right-items .mobile-narrow')
    }
    if (!anchor) return

    const html =
      '<div id="qf-widget">' +
      '<label>Quick Filter:</label>' +
      '<div class="qf-combo" id="qf-type-combo" data-role="type">' +
      '<input type="text" class="qf-combo-input" placeholder="Filter Type..." autocomplete="off">' +
      '<div class="qf-combo-dropdown"></div>' +
      '</div>' +
      '<div class="qf-combo" id="qf-value-combo" data-role="value">' +
      '<input type="text" class="qf-combo-input" placeholder="Value..." autocomplete="off" disabled>' +
      '<div class="qf-combo-dropdown"></div>' +
      '</div>' +
      '<div id="qf-tags"></div>' +
      '<button id="qf-apply-btn" type="button">Apply</button>' +
      '<button id="qf-clear-btn" type="button">Clear</button>' +
      '</div>'
    anchor.insertAdjacentHTML('beforebegin', html)
  }

  // ---- 解析页面 DOM 构建 FilterJSON ----
  function parseFilterDOM(doc: Document): FilterData {
    const filters: FilterType[] = []

    doc.querySelectorAll('.leftfilter-type').forEach((typeEl) => {
      const titleEl = typeEl.querySelector('.leftfilter-title')
      if (!titleEl) return

      // childNodes TEXT_NODE：避免取到内部 button/span 的文字
      let title = ''
      for (let i = 0; i < titleEl.childNodes.length; i++) {
        if (titleEl.childNodes[i].nodeType === Node.TEXT_NODE) {
          const text = (titleEl.childNodes[i].textContent || '').trim().replace(/-\s*$/, '').trim()
          if (text) {
            title = text
            break
          }
        }
      }
      if (!title) return

      const hasCheckbox = !!typeEl.querySelector('input[type="checkbox"].check-filter')

      const items: FilterItem[] = []
      typeEl.querySelectorAll<HTMLAnchorElement>('.leftfilter-item a').forEach((a) => {
        const href = a.getAttribute('href') || ''
        const qIdx = href.indexOf('?')
        let fil = qIdx >= 0 ? href.slice(qIdx + 1) : ''
        if (href.indexOf('/search') !== -1) {
          fil = fil.replace(/searchterm=&/g, '').replace(/&$/, '')
        }
        const itemTitle = (a.textContent || '').replace(/\s*\(\d+\)\s*$/, '').trim()
        if (fil && itemTitle) {
          items.push({ Title: itemTitle, fil })
        }
      })

      if (items.length) {
        filters.push({
          Title: title,
          SelectedType: hasCheckbox ? 'CheckBox' : 'Radio',
          FilterItems: items
        })
      }
    })

    const sorts: SortItem[] = []
    doc
      .querySelectorAll<HTMLAnchorElement>('.hidden-xs .dropdown-select .dropdown-item a')
      .forEach((a) => {
        const href = a.getAttribute('href') || ''
        const qIdx = href.indexOf('?')
        let fil = qIdx >= 0 ? href.slice(qIdx + 1) : ''
        if (href.indexOf('/search') !== -1) {
          fil = fil.replace(/searchterm=&/g, '').replace(/&$/, '')
        }
        const title = (a.textContent || '').trim()
        if (fil && title) {
          sorts.push({ Title: title, fil })
        }
      })

    return { Filters: filters, Sorts: sorts }
  }

  // ---- SessionStorage 缓存 + 按需 Fetch 基础页 DOM ----
  function loadFilterData(cb: (data: FilterData) => void): void {
    const key = getBaseKey()
    const cacheKey = 'qf_' + key

    let cached = null
    try {
      cached = sessionStorage.getItem(cacheKey)
    } catch (e) {
      /* ignore */
    }
    if (cached) {
      cb(JSON.parse(cached))
      return
    }

    const currentFull = location.pathname + location.search
    if (key === currentFull) {
      const data = parseFilterDOM(document)
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify(data))
      } catch (e) {
        /* ignore */
      }
      cb(data)
    } else {
      fetch(key)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html')
          const data = parseFilterDOM(doc)
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify(data))
          } catch (e) {
            /* ignore */
          }
          cb(data)
        })
        .catch(() => {
          const data = parseFilterDOM(document)
          cb(data)
        })
    }
  }

  // ---- 从当前 URL 初始化已选项 ----
  function initFromUrl(): void {
    if (!FilterData) return
    const search = location.search
    if (!search || search === '?') return

    const rawQs = search.slice(1) // 去掉 '?'

    // 提取不含 = 的主 filter 字符串（如 "color-red-category-pens"）
    let filterStr = ''
    rawQs.split('&').forEach((part) => {
      if (part.indexOf('=') < 0 && part) filterStr = part
    })

    // 1. 独立 key=val 参数的 filter（如 preproduction=1）
    FilterData.Filters.forEach((typeData) => {
      typeData.FilterItems.forEach((item) => {
        if (!item.fil || !isIndependentParam(item.fil)) return
        const eqIdx = item.fil.indexOf('=')
        const paramName = item.fil.slice(0, eqIdx)
        const paramVal = item.fil.slice(eqIdx + 1)
        if (getUrlParameter(paramName) === paramVal) {
          addFilterSelection(typeData.Title, item.Title, item.fil, typeData.SelectedType, true)
        }
      })
    })

    // 2. Sort
    const sortbyVal = getUrlParameter('sortby')
    if (sortbyVal) {
      const matchSort = FilterData.Sorts.find((s) => s.fil === 'sortby=' + sortbyVal)
      if (matchSort) addSortSelection(matchSort.Title, matchSort.fil, true)
    }

    // 3. 普通 filter codes（子串匹配）
    if (filterStr) {
      const allFilter: {
        typeTitle: string
        selectedType: 'Radio' | 'CheckBox'
        item: FilterItem
      }[] = []
      FilterData.Filters.forEach((typeData) => {
        typeData.FilterItems.forEach((item) => {
          if (!item.fil || isIndependentParam(item.fil)) return
          if (filterStr.indexOf(item.fil) >= 0) {
            allFilter.push({
              typeTitle: typeData.Title,
              selectedType: typeData.SelectedType,
              item: item
            })
          }
        })
      })
      let position = 0
      allFilter
        .sort((a, b) => b.item.fil.length - a.item.fil.length)
        .forEach((item) => {
          const idx = filterStr.indexOf(item.item.fil, position)
          if (idx >= 0) {
            addFilterSelection(
              item.typeTitle,
              item.item.Title,
              item.item.fil,
              item.selectedType,
              true
            )
            position = idx + item.item.fil.length
          }
        })
    }
  }

  // ---- 添加 Filter 选择（单选/多选处理）----
  // silent=true 时不重渲染 Tag（批量初始化时使用）
  function addFilterSelection(
    typeTitle: string,
    valueLabel: string,
    fil: string,
    selectedType: 'Radio' | 'CheckBox',
    silent = false
  ): void {
    if (selectedType === 'Radio') {
      filterSelections = filterSelections.filter((f) => f.typeTitle !== typeTitle)
    } else {
      if (filterSelections.some((f) => f.fil === fil)) return
    }
    filterSelections.push({ typeTitle, typeLabel: typeTitle, valueLabel, fil })
    if (!silent) renderTags()
  }

  function addSortSelection(label: string, fil: string, silent = false): void {
    sortSelection = { label, fil }
    if (!silent) renderTags()
  }

  // ---- 渲染已选 Tag ----
  function renderTags(): void {
    const container = document.getElementById('qf-tags')
    if (!container) return
    container.innerHTML = ''

    filterSelections.forEach((f, idx) => {
      const tag = document.createElement('span')
      tag.className = 'qf-tag'
      tag.setAttribute('data-idx', String(idx))
      tag.setAttribute('data-type', 'filter')
      tag.innerHTML =
        f.typeLabel +
        ':' +
        f.valueLabel +
        ' <span class="qf-tag-x" aria-hidden="true">\u00d7</span>'
      container.appendChild(tag)
    })

    if (sortSelection) {
      const tag = document.createElement('span')
      tag.className = 'qf-tag'
      tag.setAttribute('data-type', 'sort')
      tag.innerHTML =
        'Sort:' + sortSelection.label + ' <span class="qf-tag-x" aria-hidden="true">\u00d7</span>'
      container.appendChild(tag)
    }
  }

  // ---- Apply：构建完整 URL 并跳转 ----
  // URL 格式：/path?regularFilter1-regularFilter2&independentParam=1&sortby=12
  function applyFilters(): void {
    const basePath = getBaseKey().split('?')[0]
    const parts: string[] = []

    // search 页保留 searchterm
    const st = getUrlParameter('searchterm')
    if (st) parts.push('searchterm=' + encodeURIComponent(st))

    // 普通 filter：多个 fil 以 - 拼接为单一无键片段
    const regularFils = filterSelections
      .filter((f) => !isIndependentParam(f.fil))
      .map((f) => f.fil)
      .sort((a, b) => b.length - a.length)
    if (regularFils.length) parts.push(regularFils.join('-'))

    // 独立参数（如 preproduction=1）：各自独立 &key=val
    filterSelections.filter((f) => isIndependentParam(f.fil)).forEach((f) => parts.push(f.fil))

    // Sort
    if (sortSelection) parts.push(sortSelection.fil)

    location.href = basePath + '?' + parts.join('&')
  }

  // ---- Clear ----
  function clearAll(): void {
    filterSelections = []
    sortSelection = null
    renderTags()
    resetTypeCombo()
    resetValueCombo()
  }

  // ============================================================
  // ---- 自定义 Combobox ----
  // ============================================================

  let typeComboItems: ComboItem[] = [] // [{label, value}]
  let valueComboItems: ComboItem[] = [] // [{label, value}]
  let selectedTypeKey = '' // 当前选中的 FilterType key

  function buildTypeItems(): void {
    typeComboItems = []
    FilterData!.Filters.forEach((f) => {
      typeComboItems.push({ label: f.Title, value: f.Title })
    })
    if (FilterData!.Sorts && FilterData!.Sorts.length) {
      typeComboItems.push({ label: 'Sort By', value: '__sort__' })
    }
  }

  function buildValueItems(typeKey: string): void {
    valueComboItems = []
    if (!typeKey) return
    if (typeKey === '__sort__') {
      FilterData!.Sorts.forEach((s) => {
        valueComboItems.push({ label: s.Title, value: s.fil })
      })
    } else {
      const typeData = FilterData!.Filters.find((f) => f.Title === typeKey)
      if (typeData) {
        typeData.FilterItems.forEach((item) => {
          valueComboItems.push({ label: item.Title, value: item.fil })
        })
      }
    }
  }

  // 根据输入文字过滤并展示下拉
  function showDropdown(combo: HTMLElement, items: ComboItem[], inputText: string): void {
    const dropdown = combo.querySelector<HTMLElement>('.qf-combo-dropdown')!
    const filter = (inputText || '').toLowerCase()
    const matched = items.filter((item) => !filter || item.label.toLowerCase().indexOf(filter) >= 0)
    dropdown.innerHTML = ''
    if (!matched.length) {
      dropdown.style.display = 'none'
      return
    }
    matched.forEach((item) => {
      const opt = document.createElement('div')
      opt.className = 'qf-combo-opt'
      opt.textContent = item.label
      opt.setAttribute('data-value', item.value)
      dropdown.appendChild(opt)
    })
    dropdown.style.display = 'block'
  }

  function hideDropdown(combo: HTMLElement): void {
    const dropdown = combo.querySelector<HTMLElement>('.qf-combo-dropdown')
    if (dropdown) dropdown.style.display = 'none'
  }

  function resetTypeCombo(): void {
    selectedTypeKey = ''
    const combo = document.getElementById('qf-type-combo')
    if (!combo) return
    ;(combo.querySelector('.qf-combo-input') as HTMLInputElement).value = ''
    hideDropdown(combo)
    resetValueCombo()
  }

  function resetValueCombo(): void {
    const combo = document.getElementById('qf-value-combo')
    if (!combo) return
    const input = combo.querySelector<HTMLInputElement>('.qf-combo-input')!
    input.value = ''
    input.disabled = true
    hideDropdown(combo)
  }

  function onTypeSelected(typeKey: string, typeLabel: string): void {
    selectedTypeKey = typeKey
    const typeCombo = document.getElementById('qf-type-combo')
    if (typeCombo) {
      ;(typeCombo.querySelector('.qf-combo-input') as HTMLInputElement).value = typeLabel
      hideDropdown(typeCombo)
    }
    buildValueItems(typeKey)
    const valueCombo = document.getElementById('qf-value-combo')
    if (valueCombo) {
      const input = valueCombo.querySelector<HTMLInputElement>('.qf-combo-input')!
      input.disabled = false
      input.value = ''
      input.focus()
      showDropdown(valueCombo, valueComboItems, '')
    }
  }

  function onValueSelected(fil: string, valueLabel: string): void {
    if (!fil || !selectedTypeKey) return
    if (selectedTypeKey === '__sort__') {
      addSortSelection(valueLabel, fil)
    } else {
      const typeData = FilterData!.Filters.find((f) => f.Title === selectedTypeKey)
      const selectedType = typeData ? typeData.SelectedType : 'CheckBox'
      const st = (getUrlParameter('searchterm') || '').trim()
      if (st) {
        const encoded = encodeURIComponent(st)
        fil = fil
          .replace(new RegExp('\\?&?searchterm=' + encoded + '&?'), '')
          .replace(new RegExp('&searchterm=' + encoded + '&?'), '')
          .replace(/^[?&]+|[?&]+$/g, '')
      }
      addFilterSelection(selectedTypeKey, valueLabel, fil, selectedType)
    }
    renderTags()
    resetTypeCombo()
  }

  // ---- 绑定 Combobox 事件 ----
  function bindComboEvents(): void {
    const typeCombo = document.getElementById('qf-type-combo')
    const valueCombo = document.getElementById('qf-value-combo')
    if (!typeCombo || !valueCombo) return

    const typeInput = typeCombo.querySelector<HTMLInputElement>('.qf-combo-input')!
    const valueInput = valueCombo.querySelector<HTMLInputElement>('.qf-combo-input')!

    // --- Type Combo ---
    typeInput.addEventListener('focus', function () {
      showDropdown(typeCombo, typeComboItems, this.value)
    })
    typeInput.addEventListener('input', function () {
      selectedTypeKey = ''
      resetValueCombo()
      showDropdown(typeCombo, typeComboItems, this.value)
    })
    typeInput.addEventListener('keydown', (e: KeyboardEvent) => {
      handleDropdownKey(e, typeCombo, typeComboItems, (item) => {
        onTypeSelected(item.value, item.label)
      })
    })
    typeCombo
      .querySelector<HTMLElement>('.qf-combo-dropdown')!
      .addEventListener('mousedown', (e: MouseEvent) => {
        const opt = (e.target as HTMLElement).closest('.qf-combo-opt') as HTMLElement | null
        if (opt) {
          onTypeSelected(opt.getAttribute('data-value')!, opt.textContent!)
        }
      })

    // --- Value Combo ---
    valueInput.addEventListener('focus', function () {
      if (!selectedTypeKey) return
      showDropdown(valueCombo, valueComboItems, this.value)
    })
    valueInput.addEventListener('input', function () {
      showDropdown(valueCombo, valueComboItems, this.value)
    })
    valueInput.addEventListener('keydown', (e: KeyboardEvent) => {
      handleDropdownKey(e, valueCombo, valueComboItems, (item) => {
        onValueSelected(item.value, item.label)
      })
    })
    valueCombo
      .querySelector<HTMLElement>('.qf-combo-dropdown')!
      .addEventListener('mousedown', (e: MouseEvent) => {
        const opt = (e.target as HTMLElement).closest<HTMLElement>('.qf-combo-opt')
        if (opt) {
          onValueSelected(opt.getAttribute('data-value')!, opt.textContent!)
        }
      })

    // 点击页面其他区域时收起下拉
    document.addEventListener('mousedown', (e: MouseEvent) => {
      if (!typeCombo.contains(e.target as Node)) hideDropdown(typeCombo)
      if (!valueCombo.contains(e.target as Node)) hideDropdown(valueCombo)
    })
  }

  // 键盘方向键 / Enter / Escape 操作下拉
  function handleDropdownKey(
    e: KeyboardEvent,
    combo: HTMLElement,
    items: ComboItem[],
    onSelect: (item: ComboItem) => void
  ): void {
    const dropdown = combo.querySelector<HTMLElement>('.qf-combo-dropdown')!
    const opts = Array.from(dropdown.querySelectorAll<HTMLElement>('.qf-combo-opt'))
    const activeIdx = opts.findIndex((o) => o.classList.contains('active'))

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      const next = activeIdx < opts.length - 1 ? activeIdx + 1 : 0
      opts.forEach((o) => o.classList.remove('active'))
      if (opts[next]) {
        opts[next].classList.add('active')
        opts[next].scrollIntoView({ block: 'nearest' })
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      const prev = activeIdx > 0 ? activeIdx - 1 : opts.length - 1
      opts.forEach((o) => o.classList.remove('active'))
      if (opts[prev]) {
        opts[prev].classList.add('active')
        opts[prev].scrollIntoView({ block: 'nearest' })
      }
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const active = opts[activeIdx]
      if (active) {
        const val = active.getAttribute('data-value')!
        const label = active.textContent!
        onSelect({ value: val, label })
      }
    } else if (e.key === 'Escape') {
      hideDropdown(combo)
    }
  }

  // ---- 绑定其他事件 ----
  function bindOtherEvents(): void {
    const applyBtn = document.getElementById('qf-apply-btn')
    const clearBtn = document.getElementById('qf-clear-btn')
    const tagsDiv = document.getElementById('qf-tags')

    if (applyBtn) applyBtn.addEventListener('click', applyFilters)
    if (clearBtn) clearBtn.addEventListener('click', clearAll)

    // Tag 点击删除
    if (tagsDiv) {
      tagsDiv.addEventListener('click', (e: MouseEvent) => {
        const tag = (e.target as HTMLElement).closest<HTMLElement>('.qf-tag')
        if (!tag) return
        const type = tag.getAttribute('data-type')
        if (type === 'sort') {
          sortSelection = null
        } else {
          const idx = parseInt(tag.getAttribute('data-idx') || '-1', 10)
          if (idx >= 0) filterSelections.splice(idx, 1)
        }
        renderTags()
      })
    }
  }

  // ---- 入口 ----
  function init(): void {
    injectWidget()
    if (!document.getElementById('qf-widget')) return

    loadFilterData((data) => {
      FilterData = data
      buildTypeItems()
      initFromUrl() // 从当前 URL 恢复已选项
      renderTags()
      bindComboEvents()
      bindOtherEvents()
    })
  }

  init()
})
